import { useState } from 'react';
import CategoriesModal from './CategoriesModal';
import { Category } from '../types/offers';
import { useSegmentTracking, EVENTS } from '../hooks/useSegmentTracking';

interface CategoryFilterProps {
  selectedCategory: string | null;
  onCategorySelect: (category: string | null) => void;
  categories: Category[];
  platform: 'ios' | 'android';
  showNearby?: boolean;
}

// Main categories shown in the filter pills (excluding "All categories" and "More")
const MAIN_CATEGORIES_COUNT = 3;

export default function CategoryFilter({ selectedCategory, onCategorySelect, categories, platform, showNearby = false }: CategoryFilterProps) {
  const { track } = useSegmentTracking();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCategorySelect = (category: string, source: 'pill' | 'modal') => {
    track(EVENTS.ButtonClicked, { id: 'category-filter', category, source });
    onCategorySelect(category);
  };

  // Get category names from the Category objects
  const categoryNames = [
    'All categories',
    ...(showNearby ? ['Nearby'] : []),
    ...categories.map(category => category.attributes.name)
  ];

  // Check if the selected category is one of the main categories or from the "More" categories
  const mainCategoriesEnd = showNearby ? MAIN_CATEGORIES_COUNT + 2 : MAIN_CATEGORIES_COUNT + 1;
  const isMoreCategorySelected = selectedCategory !== null &&
    !categoryNames.slice(1, mainCategoriesEnd).includes(selectedCategory) &&
    selectedCategory !== 'More' &&
    selectedCategory !== 'All categories';

  const handleMoreClick = () => {
    setIsModalOpen(true);
    track(EVENTS.ModalOpened, { id: 'categories-modal' });
  };

  // Determine which categories to display in the filter pills
  const displayCategories = isMoreCategorySelected
    ? ['All categories', ...categoryNames.slice(1, mainCategoriesEnd - 1), selectedCategory]
    : ['All categories', ...categoryNames.slice(1, mainCategoriesEnd - 1), 'More'];

  return (
    <>
      <div className="w-full overflow-x-auto scrollbar-hide pb-2 mb-2 px-4">
        <div className="flex space-x-2">
          {displayCategories.map((category) => (
            <button
              key={category}
              onClick={() => {
                if (category === 'More') {
                  handleMoreClick();
                } else if (category === 'All categories' && selectedCategory === 'All categories') {
                  return;
                } else {
                  handleCategorySelect(selectedCategory === category ? 'All categories' : category, 'pill');
                }
              }}
              className={`
                px-4 py-2 rounded-full label-medium whitespace-nowrap
                ${selectedCategory === category
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-900 border border-shades-300'
                }
                transition-colors duration-200
              `}
            >
              {category}
              {category === 'More' && !isMoreCategorySelected && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 inline" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              )}
              {isMoreCategorySelected && category === selectedCategory && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 inline" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Categories Modal */}
      <CategoriesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelectCategory={(category) => handleCategorySelect(category, 'modal')}
        categories={categoryNames}
        platform={platform}
      />
    </>
  );
}